import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useCompany } from './CompanyContext';
import fingerprintSocketService from '../services/fingerprintSocketService';

interface MachineState {
  machineId: number;
  machineNumber: string;
  D2: number;
  D5: number;
  RPM: number;
  isOnline: boolean;
  reason: 'on' | 'fault' | 'manual' | 'ignored';
  timestamp: Date;
}

interface MachineUpdate {
  machineId: number;
  machineNumber: string;
  D2: number;
  D5: number;
  RPM: number;
  isOnline: boolean;
  reason: 'on' | 'fault' | 'manual' | 'ignored';
  timestamp: string;
}


interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  machineStates: Map<number, MachineState>;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  error: string | null;
  requestMachineStates: () => void;
  fingerprintSocketService: typeof fingerprintSocketService;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { selectedCompany } = useCompany();
  // A multi-company login follows the company picked in the switcher; everyone else
  // is pinned to their own tenant. Super-admins keep their existing behaviour (null).
  const isMultiCompany =
    !!user && user.role !== 'SUPERADMIN' && Array.isArray(user.machineTenantIds) && user.machineTenantIds.length > 0;
  const effectiveTenantId = isMultiCompany
    ? (selectedCompany?.tenantId ?? null)
    : (user?.tenantId ?? null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const [machineStates, setMachineStates] = useState<Map<number, MachineState>>(new Map());
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messageBufferRef = useRef<MachineUpdate[]>([]);
  const flushIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasRequestedInitialStatesRef = useRef(false); // Use ref to track in closures

  useEffect(() => {
    // Initialize Socket.IO connection
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:6001';
    const newSocket = io(backendUrl, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: true
    });

    setSocket(newSocket);
    setConnectionStatus('connecting');

      // Set up tenant-joined event listener - this will handle the response from join-tenant
      const handleTenantJoined = (response: { success: boolean; tenantId: number | null; role: string | null }) => {
        console.log('📨 tenant-joined event received:', response);
        if (response && response.success && !hasRequestedInitialStatesRef.current) {
          hasRequestedInitialStatesRef.current = true;
          console.log(`✅ Tenant context established: ${response.tenantId}, role: ${response.role}`);
          // NOW it's safe to request machine states
          console.log('📤 Emitting get-machine-states...');
          newSocket.emit('get-machine-states');
        } else {
          console.log('⚠️ tenant-joined event ignored:', { 
            hasResponse: !!response, 
            success: response?.success, 
            alreadyRequested: hasRequestedInitialStatesRef.current 
          });
        }
      };
      newSocket.on('tenant-joined', handleTenantJoined);

    // Connection event handlers
    newSocket.on('connect', () => {
      console.log('🔌 WebSocket connected:', newSocket.id);
      setIsConnected(true);
      setConnectionStatus('connected');
      setError(null);
      
      // Initialize fingerprint socket service
      fingerprintSocketService.initialize(newSocket);
      
      // SECURITY: Only join tenant room if user is available
      // Do NOT request machine states until tenant context is established
      // Note: user might not be available yet, so we'll rely on the separate useEffect
      console.log('🔌 Socket connected, user available:', !!user);

      // Setup flush interval for buffered messages (500ms)
      // Clear any existing interval first
      if (flushIntervalRef.current) {
        clearInterval(flushIntervalRef.current);
      }
      
      flushIntervalRef.current = setInterval(() => {
        if (messageBufferRef.current.length > 0) {
          const updates = [...messageBufferRef.current];
          messageBufferRef.current = [];
          
          console.log(`🔄 Flushing ${updates.length} buffered updates:`, updates.map(u => ({ 
            machineId: u.machineId, 
            isOnline: u.isOnline, 
            RPM: u.RPM 
          })));
          
          // Batch update state
          setMachineStates(prev => {
            const newMap = new Map(prev);
            updates.forEach(update => {
              // Get existing state to preserve machineNumber if available
              const existing = prev.get(update.machineId);
              const machineState: MachineState = {
                machineId: update.machineId,
                machineNumber: update.machineNumber || existing?.machineNumber || update.machineId.toString(),
                D2: update.D2,
                D5: update.D5,
                RPM: update.RPM,
                isOnline: update.isOnline,
                reason: update.reason,
                timestamp: new Date(update.timestamp)
              };
              // console.log(`📝 Updating machine ${update.machineId}:`, {
              //   isOnline: machineState.isOnline,
              //   RPM: machineState.RPM,
              //   reason: machineState.reason
              // });
              newMap.set(update.machineId, machineState);
            });
            console.log('✅ Updated machine states. Total machines:', newMap.size, 'Machine IDs:', Array.from(newMap.keys()));
            console.log('📊 Current states:', Array.from(newMap.values()).map(s => ({ 
              machineId: s.machineId, 
              isOnline: s.isOnline, 
              RPM: s.RPM 
            })));
            return newMap;
          });
        }
      }, 500);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('🔌 WebSocket disconnected:', reason);
      setIsConnected(false);
      setConnectionStatus('disconnected');
      // Reset request flag when disconnected
      hasRequestedInitialStatesRef.current = false;
    });

    newSocket.on('connect_error', (err) => {
      console.error('❌ WebSocket connection error:', err);
      setConnectionStatus('error');
      setError(err.message);
    });

    // Machine data event handlers with throttling
    // Buffer incoming messages instead of updating state immediately
    newSocket.on('machine-update', (data: MachineUpdate) => {
      // console.log('📨 Machine update received:', data);
      // Add to buffer instead of updating state directly
      messageBufferRef.current.push(data);
    });

    newSocket.on('machine-states', (states: Record<number, MachineState>) => {
      // console.log('📋 Machine states received:', states);
      
      // Clear any pending timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      // Debounce the state update to prevent rapid re-renders
      updateTimeoutRef.current = setTimeout(() => {
        const statesMap = new Map<number, MachineState>();
        Object.values(states).forEach(state => {
          statesMap.set(state.machineId, {
            ...state,
            timestamp: new Date(state.timestamp)
          });
        });
        
        setMachineStates(statesMap);
      }, 100); // 100ms debounce
    });

    newSocket.on('error', (errorData) => {
      console.error('❌ WebSocket error:', errorData);
      // Handle tenant context required error specifically
      if (errorData.code === 'TENANT_CONTEXT_REQUIRED') {
        console.warn('⚠️ Tenant context required - waiting for user authentication');
        // Don't set this as a fatal error, just wait for tenant join
        return;
      }
      setError(errorData.message || 'WebSocket error occurred');
    });

    // Cleanup on unmount
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      if (flushIntervalRef.current) {
        clearInterval(flushIntervalRef.current);
      }
      newSocket.off('tenant-joined', handleTenantJoined);
      fingerprintSocketService.cleanup();
      newSocket.close();
      setSocket(null);
      setIsConnected(false);
      setConnectionStatus('disconnected');
      messageBufferRef.current = [];
      hasRequestedInitialStatesRef.current = false;
    };
  }, []); // Empty deps - only run once on mount

  // Join tenant room when user becomes available or changes
  // This is separate from the socket connection setup because user might load after socket connects
  useEffect(() => {
    if (socket && isConnected && user) {
      // A multi-company login that hasn't resolved a company yet has no tenant
      // context — don't request machines until the switcher picks one.
      if (isMultiCompany && effectiveTenantId === null) {
        console.log('⏸️ Multi-company login has no company selected yet — waiting');
        setMachineStates(new Map());
        hasRequestedInitialStatesRef.current = false;
        return;
      }

      console.log(`🔌 Joining tenant room: ${effectiveTenantId}, role: ${user.role}`);
      // A new tenant context = a fresh set of machines; drop the previous company's
      // states and re-request for the newly selected one.
      hasRequestedInitialStatesRef.current = false;
      setMachineStates(new Map());

      // Emit join-tenant - the main listener set up in the main useEffect will handle the response
      socket.emit('join-tenant', {
        tenantId: effectiveTenantId,
        role: user.role || null
      });

      // Fallback: If tenant-joined event doesn't fire within 500ms, request states anyway
      // This handles cases where the event might be missed
      const fallbackTimeout = setTimeout(() => {
        if (!hasRequestedInitialStatesRef.current) {
          console.warn('⚠️ tenant-joined event not received within 500ms, using fallback');
          console.log('📤 Fallback: Emitting get-machine-states...');
          hasRequestedInitialStatesRef.current = true;
          socket.emit('get-machine-states');
        }
      }, 500);

      return () => {
        clearTimeout(fallbackTimeout);
      };
    } else {
      console.log('⚠️ Cannot join tenant:', { hasSocket: !!socket, isConnected, hasUser: !!user });
    }
  }, [socket, isConnected, user, isMultiCompany, effectiveTenantId]);

  const requestMachineStates = useCallback(() => {
    if (socket && isConnected) {
      const now = Date.now();
      // Throttle requests to prevent spam (minimum 5 seconds between requests)
      if (now - lastRequestTime > 5000) {
        socket.emit('get-machine-states');
        setLastRequestTime(now);
      }
    }
  }, [socket, isConnected, lastRequestTime]);

  const contextValue: WebSocketContextType = {
    socket,
    isConnected,
    machineStates,
    connectionStatus,
    error,
    requestMachineStates,
    fingerprintSocketService
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketContext;
